import React from "react";
import { AD_PLAN_CARDS } from "./constants";

/**
 * Step 3: advertising plan selection, Stripe Checkout, or manual "pay by other".
 */
const VendorOnboardingStepPayment = ({
  formValues,
  formErrorsVendor,
  vendorPaymentComplete,
  vendorPaymentSubmitted = false,
  paymentRejected = false,
  paymentMethod = "card",
  onPaymentMethodChange,
  paymentProofUrl = "",
  onPaymentProofChange,
  paymentProofUploading = false,
  handleSelectAdPlan,
  handlePayWithCard,
  handleSubmitPayByOther,
  handleContinueFromPayment,
  onBack,
}) => {
  const planLocked = vendorPaymentComplete || vendorPaymentSubmitted;
  const canContinue = vendorPaymentComplete || vendorPaymentSubmitted;

  return (
    <div className="col-md-12">
      <div className="bg-light rounded-3 p-3 mb-3">
        <h4 className="h5 mb-2">Payment</h4>
        <p className="small text-muted mb-3">
          Choose a 6- or 12-month vendor advertising plan, then pay securely with
          a card via Stripe Checkout or submit payment by another method for admin
          review. After payment is confirmed, continue to the agreement and signature
          step.
        </p>

        {vendorPaymentComplete && (
          <div className="alert alert-success py-2 small mb-3" role="status">
            Payment received for your selected plan. Plan selection is locked so it
            stays aligned with what you paid. Continue to the agreement and signature
            step, or use Back to review earlier steps.
          </div>
        )}

        {vendorPaymentSubmitted && !vendorPaymentComplete && (
          <div className="alert alert-info py-2 small mb-3" role="status">
            Your payment has been submitted for review. Our team will verify it before
            approving your ad. You can continue to the agreement and signature step now.
          </div>
        )}

        {paymentRejected && !vendorPaymentComplete && !vendorPaymentSubmitted && (
          <div className="alert alert-warning py-2 small mb-3" role="status">
            Your payment proof was not accepted. Please upload an updated proof (optional)
            and submit again, or pay with card instead.
          </div>
        )}

        <label className="form-label fw-semibold">Advertising plan</label>
        {planLocked && (
          <p className="small text-muted mb-2">
            You cannot change plans here after payment is submitted. Contact support if
            you need to adjust your purchase.
          </p>
        )}
        <div className="row g-3 mb-2">
          {AD_PLAN_CARDS.map((plan) => {
            const selected = formValues.adType === plan.value;
            const locked = planLocked;
            return (
              <div key={plan.value} className="col-md-6">
                <div
                  role={locked ? undefined : "button"}
                  tabIndex={locked ? -1 : 0}
                  aria-disabled={locked}
                  className={`h-100 rounded-3 border p-3 ${
                    selected
                      ? "border-primary border-2 shadow-sm bg-white"
                      : "border bg-white"
                  }`}
                  style={{
                    cursor: locked ? "default" : "pointer",
                    ...(locked && !selected ? { opacity: 0.65 } : {}),
                  }}
                  onClick={
                    locked ? undefined : () => handleSelectAdPlan(plan.value)
                  }
                  onKeyDown={
                    locked
                      ? undefined
                      : (ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            handleSelectAdPlan(plan.value);
                          }
                        }
                  }
                >
                  {locked && selected && (
                    <div className="badge bg-success text-white mb-2 small">
                      {vendorPaymentComplete ? "Paid for this plan" : "Plan locked"}
                    </div>
                  )}
                  <div className="fw-bold text-primary mb-1">{plan.title}</div>
                  <div className="small text-muted mb-2">{plan.priceLine}</div>
                  <p className="small mb-0">{plan.description}</p>
                </div>
              </div>
            );
          })}
        </div>
        {formErrorsVendor.adType && (
          <div className="text-danger small mb-0">{formErrorsVendor.adType}</div>
        )}

        <div className="mt-4 pt-3 border-top">
          <label className="form-label fw-semibold">Payment method</label>
          <div className="d-flex flex-column flex-sm-row gap-3 mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="vendorPaymentMethod"
                id="vendorPaymentMethodCard"
                value="card"
                checked={paymentMethod === "card"}
                disabled={planLocked}
                onChange={() => onPaymentMethodChange?.("card")}
              />
              <label className="form-check-label" htmlFor="vendorPaymentMethodCard">
                Pay with card (Stripe)
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="vendorPaymentMethod"
                id="vendorPaymentMethodOther"
                value="other"
                checked={paymentMethod === "other"}
                disabled={planLocked}
                onChange={() => onPaymentMethodChange?.("other")}
              />
              <label className="form-check-label" htmlFor="vendorPaymentMethodOther">
                Pay by other
              </label>
            </div>
          </div>

          {paymentMethod === "other" && !planLocked && (
            <div className="mb-3">
              <p className="small text-muted mb-2">
                Use check, wire, or another offline payment method. You may optionally
                upload a photo of your payment receipt or proof.
              </p>
              <label className="form-label small mb-1" htmlFor="vendorPaymentProof">
                Payment proof photo (optional)
              </label>
              <input
                id="vendorPaymentProof"
                type="file"
                accept="image/*"
                className="form-control form-control-sm"
                disabled={paymentProofUploading || planLocked}
                onChange={onPaymentProofChange}
              />
              {paymentProofUploading && (
                <p className="small text-muted mt-1 mb-0">Uploading…</p>
              )}
              {paymentProofUrl && !paymentProofUploading && (
                <div className="mt-2">
                  <img
                    src={paymentProofUrl}
                    alt="Payment proof preview"
                    className="rounded border"
                    style={{ maxWidth: 200, maxHeight: 140, objectFit: "contain" }}
                  />
                </div>
              )}
            </div>
          )}

          {paymentMethod === "other" && planLocked && paymentProofUrl && (
            <div className="mb-3">
              <p className="small text-muted mb-1">Payment proof on file:</p>
              <img
                src={paymentProofUrl}
                alt="Payment proof"
                className="rounded border"
                style={{ maxWidth: 200, maxHeight: 140, objectFit: "contain" }}
              />
            </div>
          )}
        </div>

        <div className="d-flex flex-column flex-sm-row flex-wrap justify-content-between align-items-stretch align-items-sm-center gap-3 mt-4 pt-3 border-top w-100">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onBack}
          >
            Back
          </button>
          <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-self-sm-end">
            {paymentMethod === "card" ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handlePayWithCard}
                disabled={vendorPaymentComplete || planLocked}
              >
                {vendorPaymentComplete ? "Paid" : "Pay with card"}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSubmitPayByOther}
                disabled={vendorPaymentComplete || vendorPaymentSubmitted}
              >
                {vendorPaymentSubmitted ? "Submitted" : "Submit for review"}
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleContinueFromPayment}
              disabled={!canContinue}
            >
              Next step
              <i className="fi-chevron-right fs-sm ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorOnboardingStepPayment;
